/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ComponentType, useContext, useEffect, useState } from 'react';

import { FormattedMessage } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import Lozenge from '@atlaskit/lozenge';
import { fg } from '@atlaskit/platform-feature-flags';
import { Text } from '@atlaskit/primitives/compiled';

import type { InvitedAppShortcutAllowedApsKey } from '../../../../common/types';
import { messages } from '../../../../common/ui/invited-app-shortcuts/messages';
import { getAppKeyByApsKey } from '../../../../common/utils/invited-app-shortcuts-utils';
import { InvitedAppShortcutsContext } from '../../../../controllers/invited-app-shortcuts-context';
import { useInvitedAppAnalytics } from '../../../../services/invited-app-shortcuts/invited-app-analytics';

interface InvitedAppShortcutLabelProps {
	apsKey: InvitedAppShortcutAllowedApsKey;
	label: string;
}

const styles = cssMap({
	container: {
		display: 'grid',
		gridTemplateColumns: 'auto 1fr',
		alignItems: 'center',
		width: '100%',
	},
	lozengeContainer: {
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'center',
		minWidth: '0px',
	},
});

export const InvitedAppShortcutLabel = ({
	label,
	apsKey,
}: InvitedAppShortcutLabelProps): JSX.Element => {
	const { impressedProducts } = useContext(InvitedAppShortcutsContext);
	const hasAlreadySeenThisInvitedAppShortcut = impressedProducts[apsKey];
	const [AnimatedLozengeComponent, setAnimatedLozengeComponent] =
		useState<ComponentType<any> | null>(null);
	const { fireViewedAnimatedLozenge } = useInvitedAppAnalytics();

	useEffect(() => {
		// Keep SSR and initial hydration light by rendering static Lozenge first.
		if (hasAlreadySeenThisInvitedAppShortcut) {
			return;
		}
		// Load animation code only for users who can actually see the animated treatment.
		void import(
			/* webpackChunkName: "@atlassian/invited-app-shortcut-animated-lozenge" */ './animated-lozenge/main'
		).then((module) => {
			setAnimatedLozengeComponent(module.default as ComponentType<any>);
		});
	}, [hasAlreadySeenThisInvitedAppShortcut]);

	useEffect(() => {
		if (AnimatedLozengeComponent && fg('puma-1895-fire-viewed-animated-lozenge')) {
			fireViewedAnimatedLozenge({ app: getAppKeyByApsKey(apsKey) });
		}
	}, [AnimatedLozengeComponent, fireViewedAnimatedLozenge, apsKey]);

	const LozengeComponent =
		!hasAlreadySeenThisInvitedAppShortcut && AnimatedLozengeComponent
			? AnimatedLozengeComponent
			: Lozenge;
	const shouldRenderLozenge =
		hasAlreadySeenThisInvitedAppShortcut || AnimatedLozengeComponent !== null;

	return (
		<div css={styles.container}>
			<Text>{label}</Text>
			<div css={styles.lozengeContainer}>
				{shouldRenderLozenge ? (
					<LozengeComponent appearance="inprogress">
						<FormattedMessage {...messages.invitedLozenge} />
					</LozengeComponent>
				) : null}
			</div>
		</div>
	);
};
